import React, { useEffect, useMemo, useState } from "react";
import {
  GeoJSON,
  MapContainer,
  TileLayer,
} from "react-leaflet";

import "leaflet/dist/leaflet.css";

import {
  getDepartmentByCode,
  normalizeDepartmentCode,
} from "../utils/kataUtils";

const getFillColor = (count) => {
  if (count >= 10) return "#c2410c";
  if (count >= 6) return "#ea580c";
  if (count >= 3) return "#f97316";
  if (count >= 1) return "#fdba74";

  return "#e2e8f0";
};

export default function CarteFrance({
  departmentCounts,
  onDepartmentSelect,
}) {
  const [geoJson, setGeoJson] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const loadGeoJson = async () => {
      try {
        setError("");

        const response = await fetch("/departements.geojson", {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(
            "Le fichier cartographique est indisponible."
          );
        }

        const data = await response.json();
        setGeoJson(data);
      } catch (loadError) {
        if (loadError.name !== "AbortError") {
          setError(loadError.message);
        }
      }
    };

    loadGeoJson();

    return () => {
      controller.abort();
    };
  }, []);

  const normalizedCounts = useMemo(() => {
    return Object.entries(departmentCounts || {}).reduce(
      (acc, [code, count]) => {
        acc[normalizeDepartmentCode(code)] = Number(count);
        return acc;
      },
      {}
    );
  }, [departmentCounts]);

  const mapKey = useMemo(
    () => JSON.stringify(normalizedCounts),
    [normalizedCounts]
  );

  const getFeatureCode = (feature) =>
    normalizeDepartmentCode(
      feature?.properties?.code ||
        feature?.properties?.CODE_DEPT ||
        feature?.properties?.code_insee
    );

  const styleFeature = (feature) => {
    const code = getFeatureCode(feature);
    const count = normalizedCounts[code] || 0;

    return {
      fillColor: getFillColor(count),
      fillOpacity: count > 0 ? 0.82 : 0.45,
      color: count > 0 ? "#9a3412" : "#94a3b8",
      weight: count > 0 ? 1.6 : 1,
    };
  };

  const onEachFeature = (feature, layer) => {
    const code = getFeatureCode(feature);
    const count = normalizedCounts[code] || 0;
    const department = getDepartmentByCode(code);

    const departmentName =
      department?.departement ||
      feature?.properties?.nom ||
      "Département";

    layer.bindTooltip(
      `
        <div style="min-width: 150px">
          <strong>${code} · ${departmentName}</strong><br />
          ${count} annonce${count > 1 ? "s" : ""}
        </div>
      `,
      {
        sticky: true,
      }
    );

    layer.on({
      mouseover: (event) => {
        event.target.setStyle({
          weight: 3,
          color: "#0f172a",
        });
      },

      mouseout: (event) => {
        event.target.setStyle(styleFeature(feature));
      },

      click: () => {
        if (count > 0) {
          onDepartmentSelect(code);
        }
      },
    });
  };

  if (error) {
    return (
      <div className="rounded-[2rem] border border-red-200 bg-red-50 p-8 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
        {error}
      </div>
    );
  }

  if (!geoJson) {
    return (
      <div className="flex h-[520px] items-center justify-center rounded-[2rem] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900">
        <span className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-orange-500" />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-2 shadow-[0_18px_50px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-900">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3 px-3 py-2">
        <div>
          <h3 className="font-semibold text-slate-950 dark:text-white">
            Répartition des annonces
          </h3>

          <p className="text-xs text-slate-500 dark:text-slate-400">
            Cliquez sur un département coloré pour afficher
            ses annonces.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
          <span className="h-3 w-3 rounded-sm bg-slate-200" />
          Aucune
          <span className="ml-2 h-3 w-3 rounded-sm bg-orange-300" />
          Quelques-unes
          <span className="ml-2 h-3 w-3 rounded-sm bg-orange-700" />
          Nombreuses
        </div>
      </div>

      <MapContainer
        center={[46.603354, 1.888334]}
        zoom={6}
        minZoom={5}
        maxZoom={10}
        scrollWheelZoom={false}
        className="h-[520px] w-full rounded-[1.5rem]"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />

        <GeoJSON
          key={mapKey}
          data={geoJson}
          style={styleFeature}
          onEachFeature={onEachFeature}
        />
      </MapContainer>
    </div>
  );
}